import React from 'react'
import { Paragraph } from 'components/primitives'
import { ProviderNames } from 'hooks/dispatch/utils'
import { useTranslation } from 'react-i18next'
import { TicketCharges } from 'types/ticket'
import colors from 'theme/colors'
import { useSelector } from 'react-redux'
import { selectFormsState, selectTabsState, selectTicket } from 'redux/appStore'
import { TireCondition } from '../../forms/CallForm/TireRepair/TireConditionRadio'
import { useDamageWaiver, useProtectionWaiver } from '../../hooks/tickets'
import { isEmpty } from 'lodash-es'

export const FeesByServiceType: Record<TicketCharges, Record<ProviderNames, number>> = {
  [TicketCharges.KEY_LOCK_OUT_CHARGE]: {
    [ProviderNames.AAA]: 70.0,
    [ProviderNames.URGENTLY]: 70.0,
    [ProviderNames.ALLSTATE]: 77.0,
  },
  [TicketCharges.JUMP_START_CHARGE]: {
    [ProviderNames.AAA]: 70.0,
    [ProviderNames.URGENTLY]: 62.0,
    [ProviderNames.ALLSTATE]: 70.0,
  },
  [TicketCharges.FLAT_TIRE_CHARGE]: {
    [ProviderNames.AAA]: 70.0,
    [ProviderNames.URGENTLY]: 72.0,
    [ProviderNames.ALLSTATE]: 78.0,
  },
  [TicketCharges.FUEL_DELIVERY_CHARGE]: {
    [ProviderNames.AAA]: 70.0,
    [ProviderNames.URGENTLY]: 92.0,
    [ProviderNames.ALLSTATE]: 79.0,
  },
  [TicketCharges.LOST_KEYS_CHARGE]: {
    [ProviderNames.AAA]: 100.0,
    [ProviderNames.URGENTLY]: 100.0,
    [ProviderNames.ALLSTATE]: 100.0,
  },
  [TicketCharges.IMPOUND_OR_TOW_FEE]: {
    [ProviderNames.AAA]: 138.0,
    [ProviderNames.URGENTLY]: 114.0,
    [ProviderNames.ALLSTATE]: 127.0,
  },
  [TicketCharges.OTHER_CHARGE]: {
    [ProviderNames.AAA]: 0,
    [ProviderNames.URGENTLY]: 0,
    [ProviderNames.ALLSTATE]: 0,
  },
}

type Props = {
  provider?: ProviderNames
}

const waiverFeeStyle = { color: colors.cardinal, fontWeight: 'bold' }

function WaiverFee({ provider }: Props) {
  const { t } = useTranslation()
  const getFeesFromForm = useGetFeesFromForm()
  const amount = aggregateFees(getFeesFromForm, provider)

  const label = provider && !isNaN(amount) ? t('waivers.feesExact', { amount }) : t('waivers.feesEstimate', { amount })
  return (
    <>
      <Paragraph style={waiverFeeStyle}>{label}</Paragraph>
      <br />
    </>
  )
}

export const MaxFeeByServiceType: Record<TicketCharges, number> = Object.fromEntries(
  Object.entries(FeesByServiceType).map(([k, v]) => [k, Math.max(...Object.values(v))]),
) as Record<TicketCharges, number>

export function aggregateFees(charges: TicketCharges[], provider?: ProviderNames) {
  let totalCharges = 0.0
  for (const charge of charges) {
    if (provider) {
      totalCharges += FeesByServiceType[charge]?.[provider] ?? 0
    } else {
      totalCharges += MaxFeeByServiceType[charge]
    }
  }

  return totalCharges
}

export function getTabStateByName(tabs: any, name: String) {
  return tabs.find((tab) => tab.name === name).enabled
}

export const useGetFeesFromForm = () => {
  const { lockout, jumpstart, tires, fuel, workflow } = useSelector(selectFormsState) as any
  const tabs = useSelector(selectTabsState) as any
  const { vehicle } = useSelector(selectTicket)
  const equipmentInfo = vehicle?.equipmentInfo
  const hasSpareTire =
    Array.isArray(equipmentInfo) &&
    equipmentInfo.some(({ shortDescription }) => shortDescription && shortDescription === 'Spare tire')
  const hasRoadsideProtection = useProtectionWaiver()
  const isDamageWaived = useDamageWaiver()

  /** --------- Has Roadside Assistance --------- */
  if (hasRoadsideProtection) {
    return []
  }

  /** ---------------- Lockout ---------------- */
  if ((!isEmpty(lockout) && getTabStateByName(tabs, 'Keys')) || workflow.includes('Keys')) {
    if (lockout?.lostKeys === 'yes') {
      if (isDamageWaived) return []

      return [TicketCharges.LOST_KEYS_CHARGE, TicketCharges.IMPOUND_OR_TOW_FEE]
    }

    if (lockout?.keysLocation === 'trunk') {
      if (lockout?.trunkAccess === 'yes') {
        return [TicketCharges.KEY_LOCK_OUT_CHARGE]
      }

      if (lockout?.trunkAccess === 'no') {
        if (isDamageWaived) return []

        return [TicketCharges.IMPOUND_OR_TOW_FEE]
      }
    }

    if (lockout?.keysLocation === 'cabin') {
      return [TicketCharges.KEY_LOCK_OUT_CHARGE]
    }

    return []
  }

  /** ---------------- Jumpstart ---------------- */
  if ((!isEmpty(jumpstart) && getTabStateByName(tabs, 'Start')) || workflow.includes('Start')) {
    switch (jumpstart?.vehicleStatus) {
      case 'noLightsOrRadio':
        return [TicketCharges.JUMP_START_CHARGE]

      case 'workingLightsAndRadio':
        if (jumpstart?.noise === 'turnsOver') {
          return jumpstart?.outOfFuel === 'yes' ? [TicketCharges.FUEL_DELIVERY_CHARGE] : [TicketCharges.JUMP_START_CHARGE]
        }

        if (jumpstart?.noise === 'clicking') {
          return [TicketCharges.JUMP_START_CHARGE]
        }

        return []

      case 'keysWontTurn':
      default:
        return []
    }
  }

  /** ---------------- Tires ---------------- */
  if ((!isEmpty(tires) && getTabStateByName(tabs, 'Tires')) || workflow.includes('Tires')) {
    switch (tires?.condition) {
      case TireCondition.FLAT: // Completely flat
        return hasSpareTire && tires?.changeNeeded === 'yes' ? [TicketCharges.FLAT_TIRE_CHARGE] : []

      case TireCondition.MULTIPLEFLATS: // Two Flats
        if (isDamageWaived) return []

        return [TicketCharges.IMPOUND_OR_TOW_FEE]

      case TireCondition.DEFLATING: // Losing air
      case TireCondition.STOLENTIRES: // Three or More Tires Flat/Stolen
      default:
        return []
    }
  }

  /** ---------------- Fuel ---------------- */
  if ((!isEmpty(fuel) && getTabStateByName(tabs, 'Fuel')) || workflow.includes('Fuel')) {
    return [TicketCharges.FUEL_DELIVERY_CHARGE]
  }

  return []
}

export default WaiverFee
